import React from 'react';
import { Link } from 'react-router-dom';
import {
  HeartPulse,
  Activity,
  Droplets,
  Battery,
  Watch,
  Apple,
  Move,
  ShieldCheck,
  Sun,
  Factory,
  Truck,
  Stethoscope,
  Cpu,
  Building2,
  ArrowRight,
} from 'lucide-react';
import { SectionHeading, FeatureCard, ImageBlock, KpiTile } from '@/components/Pieces';
import ContactForm from '@/components/ContactForm';
import FAQ from '@/components/FAQ';
import usePageMeta from '@/hooks/usePageMeta';
import { img } from '@/lib/site';

const SYSTEMS = [
  { icon: Battery, title: 'Workforce Fatigue Management', desc: 'Track and reduce on-shift fatigue across teams and roles.' },
  { icon: Move, title: 'Mobility Systems', desc: 'Daily mobility blocks engineered for industrial and office bodies.' },
  { icon: HeartPulse, title: 'Recovery Strategies', desc: 'Active recovery, sleep, breathwork, and shift-recovery scoring.' },
  { icon: ShieldCheck, title: 'Injury Reduction', desc: 'Workplace durability protocols built from sports science.' },
  { icon: Watch, title: 'Readiness Tracking', desc: 'Wearable-driven readiness scores for individuals and teams.' },
  { icon: Activity, title: 'Workplace Durability', desc: 'Role-specific durability programming for high-load environments.' },
  { icon: Droplets, title: 'Hydration Education', desc: 'On-floor hydration systems with measurable compliance.' },
  { icon: Apple, title: 'Nutrition Support', desc: 'Performance fueling for shift workers and corporate teams.' },
  { icon: Sun, title: 'Energy Management', desc: 'Light, sleep, and circadian planning for variable schedules.' },
];

const INDUSTRIES = [
  { icon: Factory, name: 'Manufacturing' },
  { icon: Building2, name: 'Warehousing' },
  { icon: Truck, name: 'Logistics' },
  { icon: Stethoscope, name: 'Healthcare' },
  { icon: Cpu, name: 'Technology' },
  { icon: Activity, name: 'Mixed Workforce' },
];

export default function WorkforceSystemsPage() {
  usePageMeta({
    title: 'Workforce Performance Systems | Employee Energy, Recovery & Retention | Ball Raw Performance',
    description:
      'Workforce performance systems that improve employee energy, recovery, readiness, and retention — and reduce burnout. Built for HR, Operations, Safety, and Wellness leaders across manufacturing, logistics, healthcare, and corporate teams.',
    path: '/workforce-systems',
  });
  return (
    <div data-testid="page-workforce-systems">
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={img('divisions_workforce')} alt="Workforce performance" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <p className="brp-overline brp-reveal">Workforce Performance Systems</p>
          <h1 className="brp-reveal brp-delay-1 font-display uppercase font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.92] mt-6 max-w-4xl">
            Better Energy. Stronger Retention. <span className="text-[#D4AF37]">Healthier Workforce.</span>
          </h1>
          <p className="brp-reveal brp-delay-2 mt-8 text-lg md:text-xl text-gray-300 max-w-2xl">
            Workforce performance systems that improve employee energy, recovery, readiness, and retention — and reduce burnout. Built for HR, Operations, Safety, and Wellness leaders across industrial and corporate environments.
          </p>
          <div className="brp-reveal brp-delay-3 mt-10 flex flex-wrap gap-4">
            <Link to="/partnerships#contact" className="btn-brp-primary" data-testid="workforce-cta-plan">
              Build a Workforce Performance Plan <ArrowRight size={16} />
            </Link>
            <Link to="/partnerships" className="btn-brp-outline-light" data-testid="workforce-cta-partner">
              Become a Partner
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="The Systems"
            title="Performance Infrastructure For Working Bodies."
            subtitle="The same recovery, mobility, and readiness systems used by elite teams — adapted for shift workers, technicians, clinicians, and corporate teams to support employee energy, wellbeing, burnout reduction, and long-term retention."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SYSTEMS.map((s, i) => (
              <FeatureCard key={s.title} icon={s.icon} title={s.title} desc={s.desc} accent testId={`system-${i}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <ImageBlock src={img('biometric_dashboard')} alt="Biometric dashboard" ratio="aspect-[4/3]" caption="Wearable Integration" />
          </div>
          <div className="lg:col-span-6">
            <SectionHeading
              light
              overline="Biometric Wearable Integration"
              title="Readiness Becomes A KPI."
              subtitle="We integrate wearables, HRV, sleep, and movement data into workforce-scale dashboards — so your operations team can see readiness like they see OEE."
            />
            <ul className="mt-8 space-y-3">
              {[
                'Multi-vendor wearable integration',
                'Team-level readiness aggregation',
                'Shift recovery scoring',
                'Privacy-first data governance',
                'HR & Ops reporting cadence',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-gray-300">
                  <span className="mt-1 w-2 h-2 bg-[#D4AF37] inline-block flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Industries Served"
            title="Where We Deploy."
          />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#E5E5E5]">
            {INDUSTRIES.map((it, i) => (
              <div key={it.name} className="bg-white p-8 text-center hover:bg-[#0A0A0A] transition group cursor-default" data-testid={`industry-${i}`}>
                <it.icon size={32} strokeWidth={1.5} className="mx-auto text-[#0A0A0A] group-hover:text-[#D4AF37] transition" />
                <div className="mt-4 font-display uppercase tracking-wider text-sm group-hover:text-white transition">{it.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Outcomes"
            title="Measurable. Operational. Repeatable."
          />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#D4AF37]/30">
            <KpiTile value="-31" suffix="%" label="Fatigue Reduction" testId="kpi-w-fatigue" />
            <KpiTile value="84" label="Avg Readiness Score" testId="kpi-w-readiness" />
            <KpiTile value="92" suffix="%" label="Recovery Compliance" testId="kpi-w-recovery" />
            <KpiTile value="+18" suffix="%" label="Retention Lift" testId="kpi-w-retention" />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <FAQ
            testIdPrefix="workforce-faq"
            overline="Frequently Asked"
            title="Frequently Asked Questions"
            items={[
              {
                q: 'What is Workforce Performance and why does it matter?',
                a: 'Workforce Performance is the ability of employees to perform at their best while supporting productivity, engagement, and organizational success. Strong workforce performance helps organizations build healthier teams and achieve long term business goals.',
              },
              {
                q: 'How does Workforce Performance support business performance?',
                a: 'Organizations with engaged and supported employees often experience stronger collaboration, improved morale, and better organizational outcomes. Our programs are designed to help employers build a workforce that performs consistently over time.',
              },
              {
                q: 'How is this different from a traditional employee wellness program?',
                a: 'Traditional programs often depend on employees participating on their own. Workforce Performance Systems provide structured coaching, accountability, and experiences that become part of your workplace culture.',
              },
              {
                q: 'Can Workforce Performance support both office employees and frontline workers?',
                a: 'Yes. Our programs are designed for office environments, remote teams, hybrid workplaces, and physically demanding industries including manufacturing, logistics, and warehousing.',
              },
              {
                q: 'How do you measure success?',
                a: 'Success is measured using the goals established before implementation. Organizations may track participation, engagement, employee feedback, retention, and other workforce performance indicators.',
              },
              {
                q: 'How quickly can a Workforce Performance program launch?',
                a: 'Every organization is different, but implementation begins after planning and onboarding are complete. We work with your team to create a timeline that fits your operations.',
              },
            ]}
          />
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#0A0A0A] text-white" id="contact">
        <div className="max-w-[900px] mx-auto">
          <ContactForm
            formType="workforce_plan"
            variant="dark"
            title="Build A Workforce Performance Plan"
            subtitle="Share your workforce profile. We will return a tailored recovery, readiness, and durability plan."
            showRole
            showEmployees
            showIndustry
            ctaLabel="Request Plan"
            testIdPrefix="workforce-contact"
          />
        </div>
      </section>
    </div>
  );
}
